import { adaptCoverage, emptyCoverage } from "./coverage";
import {
  type KnowledgeViewKey,
  type QualityDimensionCount,
  type QualityGrade,
  type QualityScope,
  type QualitySummary,
  type QualityViewOutcome,
  type QualityViewSummary,
} from "./types";

/**
 * Normalise the raw `GET /v1/runs/{id}/quality` payload (the backend
 * `KnowledgeQualityManifest`, i.e. `quality/index.json`) into the
 * {@link QualitySummary} the UI consumes.
 *
 * Defensive about missing fields, and deliberately *not* defensive about a
 * missing score. The backend publishes `overall_score` and `grade` only when
 * every expected Knowledge View was scored, so their absence is the run
 * telling us it could not reach a headline. Substituting `0` — as this adapter
 * used to — turned that into an F, which is a worse lie than no grade: it
 * reports a judgement about the environment when the truth is that the
 * assurance run did not finish.
 */
export function adaptQuality(input: unknown): QualitySummary {
  if (!isRecord(input)) {
    throw new Error("Quality payload is not an object");
  }
  const rawViews = Array.isArray(input.views) ? input.views : [];
  const rawCounts = Array.isArray(input.counts_by_dimension)
    ? input.counts_by_dimension
    : [];
  return {
    schemaVersion: numberOr(input.schema_version, 1),
    provider: stringOr(input.provider, "aws"),
    ...(typeof input.generated_at === "string"
      ? { generatedAt: input.generated_at }
      : {}),
    overallScore: numberOrNull(input.overall_score),
    grade: gradeOrNull(input.grade),
    meanScoredViewScore: numberOrNull(input.mean_scored_view_score),
    weakestViewScore: numberOrNull(input.weakest_view_score),
    viewCoverage:
      input.view_coverage === undefined
        ? emptyCoverage("view")
        : adaptCoverage(input.view_coverage, "view"),
    scope: adaptScope(input.scope_statement),
    ...(typeof input.strongest_view === "string"
      ? { strongestView: input.strongest_view as KnowledgeViewKey }
      : {}),
    ...(typeof input.weakest_view === "string"
      ? { weakestView: input.weakest_view as KnowledgeViewKey }
      : {}),
    findingCount: numberOr(input.finding_count, 0),
    countsByDimension: rawCounts
      .map(adaptDimensionCount)
      .filter((c): c is QualityDimensionCount => c !== null),
    views: rawViews
      .map(adaptViewSummary)
      .filter((v): v is QualityViewSummary => v !== null),
  };
}

const VIEW_OUTCOMES: readonly QualityViewOutcome[] = [
  "scored",
  "materially_weak",
  "failed",
  "missing",
];

function adaptViewSummary(raw: unknown): QualityViewSummary | null {
  if (!isRecord(raw)) return null;
  const key =
    typeof raw.view === "string" ? (raw.view as KnowledgeViewKey) : null;
  if (!key) return null;
  const outcome = VIEW_OUTCOMES.find((o) => o === raw.outcome) ?? "scored";
  return {
    key,
    displayName: stringOr(raw.display_name, key),
    outcome,
    overallScore: numberOrNull(raw.overall_score),
    grade: gradeOrNull(raw.grade),
    personaCoverageRatio: numberOrNull(raw.persona_coverage_ratio),
    findingCount: numberOr(raw.finding_count, 0),
  };
}

function adaptScope(raw: unknown): QualityScope {
  if (!isRecord(raw)) return { judges: [], doesNotJudge: [], note: "" };
  return {
    judges: stringList(raw.judges),
    doesNotJudge: stringList(raw.does_not_judge),
    note: stringOr(raw.note, ""),
  };
}

function adaptDimensionCount(raw: unknown): QualityDimensionCount | null {
  if (!isRecord(raw) || typeof raw.dimension !== "string") return null;
  return { dimension: raw.dimension, count: numberOr(raw.count, 0) };
}

/**
 * Mirror of backend `grade_for_score`: A≥90 B≥80 C≥70 D≥60 else F.
 *
 * `null` in, `null` out. Kept for surfaces that hold a score without a grade
 * beside it; the manifest's own `grade` is authoritative when present, because
 * the backend caps it at the weakest view's and this function cannot know that.
 */
export function gradeForScore(score: number | null): QualityGrade | null {
  if (score === null || !Number.isFinite(score)) return null;
  if (score >= 90) return "A";
  if (score >= 80) return "B";
  if (score >= 70) return "C";
  if (score >= 60) return "D";
  return "F";
}

function gradeOrNull(raw: unknown): QualityGrade | null {
  return raw === "A" || raw === "B" || raw === "C" || raw === "D" || raw === "F"
    ? raw
    : null;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function stringOr(v: unknown, fallback: string): string {
  return typeof v === "string" && v.length > 0 ? v : fallback;
}

function stringList(v: unknown): readonly string[] {
  return Array.isArray(v)
    ? v.filter((item): item is string => typeof item === "string")
    : [];
}

function numberOr(v: unknown, fallback: number): number {
  return typeof v === "number" && Number.isFinite(v) ? v : fallback;
}

function numberOrNull(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}
